import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { FormsService } from '../services/forms.service';
import { AuthService } from '../services/auth.service';

/**
 * 4.1 (#91, DCM-364): the registration wizard is only open while the enrollment
 * collects forms. Once the family submitted to the centre (or the centre sent it
 * back, which keeps the status `enrolling`), editing is closed: a submitted
 * enrollment's forms open READ-ONLY from the child card instead.
 *
 * A guard because the wizard is deep-linkable: typing the URL after submit must
 * not reopen an editable form.
 *
 * Fail CLOSED: a load error cannot prove the enrollment is still open, so the
 * parent lands on the read-only view (the same one the guard redirects to).
 */
export const enrollmentOpenGuard: CanActivateFn = (route): ReturnType<CanActivateFn> => {
  const forms = inject(FormsService);
  const router = inject(Router);
  const auth = inject(AuthService);

  const enrollmentId = Number(route.paramMap.get('enrollmentId'));
  const readOnly = (): UrlTree => router.parseUrl(`/dashboard/enrollment?readonly=${enrollmentId}`);

  // No auth yet: authGuard owns that decision; don't double-redirect.
  if (!auth.isAuthenticated) return true;
  if (!enrollmentId) return readOnly();
  // F3 (2.3, fix-enrollment-followups 2026-10-28): the parent may open the
  // Application READ-ONLY after submitting — the wizard renders it browse-only
  // (?review=1) exactly as it does on an enrolling draft's review page.
  if (route.queryParamMap.get('review') === '1') return true;

  return forms.getEnrollmentForms(enrollmentId).pipe(
    map((d): boolean | UrlTree => (d?.status === 'enrolling' ? true : readOnly())),
    catchError(() => of(readOnly())),
  );
};
